// "Alles naar vandaag" — de achterstallige lane in één keer opschuiven.
//
// Bewust mét bevestiging en mét vinkjes: dit verzet in één klik tien echte taken in
// Airtable, waar collega's op plannen. En bewust zonder alles-of-niets: Airtable faalt
// per batch, en een half geslaagde verplaatsing terugdraaien is riskanter dan hem tonen.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, List, ListItem, ListItemButton, ListItemText, Typography,
} from '@mui/material';

import { verschuifWerkdagen, werkdagenInBereik } from '../../utils/dagstartLanes';

/**
 * Naar vandaag verschuiven mét behoud van de duur: een taak van drie werkdagen die
 * blijft liggen is nog steeds drie werkdagen werk. Alleen de startdatum verspringt.
 */
export function naarVandaag(taak, vandaagISO) {
  const duur = Math.max(1, werkdagenInBereik(taak.startDate, taak.endDate).length);
  return {
    id: taak.id,
    naam: taak.name,
    StartDateUitvoerder: vandaagISO,
    EndDateUitvoerder: verschuifWerkdagen(vandaagISO, duur - 1),
  };
}

export default function BulkNaarVandaagDialog({ open, taken, vandaagISO, onSluit, onBevestig }) {
  const [gekozen, setGekozen] = useState(() => new Set());
  const [bezig, setBezig] = useState(false);
  const [uitslag, setUitslag] = useState(null);

  // Bij openen alles aanvinken — dat is de bedoeling van de knop; uitvinken is de
  // uitzondering ("die ene wacht op de klant, die laten we staan").
  useEffect(() => {
    if (open) {
      setGekozen(new Set((taken || []).map((t) => t.id)));
      setUitslag(null);
    }
  }, [open, taken]);

  const items = useMemo(
    () => (taken || []).filter((t) => gekozen.has(t.id)).map((t) => naarVandaag(t, vandaagISO)),
    [taken, gekozen, vandaagISO]
  );

  const wissel = (id) => setGekozen((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const bevestig = async () => {
    setBezig(true);
    const res = await onBevestig(items);
    setBezig(false);
    if (res?.mislukt?.length) setUitslag(res);
    else onSluit();
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onSluit} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}>
        Achterstallige taken naar vandaag
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {uitslag ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {uitslag.gelukt.length} verzet, {uitslag.mislukt.length} niet gelukt.
              De rest staat wél goed — probeer deze opnieuw of pas ze in Airtable aan.
            </Alert>
            <List dense disablePadding>
              {uitslag.mislukt.map((m) => (
                <ListItem key={m.id} disablePadding sx={{ px: 1 }}>
                  <ListItemText primary={m.naam || m.id} secondary={m.reden} />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <>
            <Typography sx={{ px: 2, pt: 1.5, fontSize: '0.8rem', color: '#556071' }}>
              De startdatum wordt vandaag; de duur blijft gelijk. Deadlines worden niet aangeraakt.
            </Typography>
            <List dense>
              {(taken || []).map((t) => {
                const nieuw = naarVandaag(t, vandaagISO);
                return (
                  <ListItem key={t.id} disablePadding>
                    <ListItemButton onClick={() => wissel(t.id)} dense>
                      <Checkbox edge="start" checked={gekozen.has(t.id)} tabIndex={-1} disableRipple size="small" />
                      <ListItemText
                        primary={`${t.project ? `${t.project} · ` : ''}${t.name}`}
                        secondary={`${t.startDate || '—'} → ${nieuw.StartDateUitvoerder} t/m ${nieuw.EndDateUitvoerder}`}
                        primaryTypographyProps={{ fontSize: '0.82rem' }}
                        secondaryTypographyProps={{ fontSize: '0.72rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onSluit} disabled={bezig} sx={{ textTransform: 'none' }}>
          {uitslag ? 'Sluiten' : 'Annuleren'}
        </Button>
        {!uitslag && (
          <Button
            variant="contained"
            onClick={bevestig}
            disabled={bezig || items.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {bezig ? 'Bezig…' : `${items.length} ${items.length === 1 ? 'taak' : 'taken'} verzetten`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
